import React, { useMemo, useState } from 'react';
import {
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    Button,
    Typography
} from '@mui/material';
import PostData from '../../services/PostData';
import CloseIcon from '@mui/icons-material/Close';
import { IconButton } from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add'
import CheckOutlinedIcon from '@mui/icons-material/CheckOutlined';

export default function SplitOrderDialog({ record, close, onSuccess }) {
    const orderRecord = Array.isArray(record) ? record[0] : record;
    const originalItemTitle = orderRecord?.fields?.["Item Title"] || orderRecord?.fields?.["Item ID"] || '';
    const recordId = orderRecord?.id || '';
    const mongoId = orderRecord?.mongoId || '';
    const orderNum = orderRecord?.fields?.["Order Number"] || '';
    const originalQuantity = Math.max(1, Math.floor(Number(orderRecord?.fields?.["Quantity"]) || 1));
    const [successSubmit, setSuccessSubmit] = useState(false);
    const [submitError, setSubmitError] = useState('');
    const [splitShipments, setSplitShipments] = useState([
        { id: 1, quantity: 1 },
        { id: 2, quantity: Math.max(1, originalQuantity - 1) },
    ]);

    const [submitLoad, setSubmitLoad] = useState(false);

    const totalAllocated = useMemo(
        () => splitShipments.reduce((sum, s) => sum + (Number(s.quantity) || 0), 0),
        [splitShipments]
    );
    const remaining = originalQuantity - totalAllocated;
    const canSubmit =
        splitShipments.length >= 2 &&
        remaining === 0 &&
        splitShipments.every((s) => Number(s.quantity) >= 1) &&
        Boolean(mongoId);

    const handleAddShipment = () => {
        if (remaining <= 0) return;
        setSplitShipments((prev) => [
            ...prev,
            { id: Date.now(), quantity: remaining },
        ]);
    };

    const handleRemoveShipment = (idToRemove) => {
        if (splitShipments.length <= 2) return;
        setSplitShipments((prev) => prev.filter((s) => s.id !== idToRemove));
    };

    const handleShipmentQuantityChange = (idToUpdate, newQuantity) => {
        const parsedQuantity = parseInt(newQuantity, 10);
        if (Number.isNaN(parsedQuantity) || parsedQuantity < 1) return;

        setSplitShipments((prev) => {
            const updatedShipments = prev.map((s) =>
                s.id === idToUpdate ? { ...s, quantity: parsedQuantity } : s
            );
            const total = updatedShipments.reduce((sum, s) => sum + s.quantity, 0);
            if (total <= originalQuantity) return updatedShipments;

            return prev.map((s) => {
                if (s.id !== idToUpdate) return s;
                const otherTotal = prev
                    .filter((p) => p.id !== idToUpdate)
                    .reduce((sum, p) => sum + p.quantity, 0);
                return { ...s, quantity: Math.max(1, originalQuantity - otherTotal) };
            });
        });
    };

    const handleSubmitSplitOrder = async () => {
        if (!canSubmit) return;
        setSubmitLoad(true);
        setSubmitError('');
        const payload = {
            splitShipments: splitShipments.map((s) => ({
                id: s.id,
                quantity: Number(s.quantity),
            })),
            orderNumber: orderNum,
            recordId,
            mongoId,
        };
        const { success, error, data } = await PostData(
            payload,
            'broylesllc/shipping/split-order-label',
            null
        );
        setSubmitLoad(false);
        if (success) {
            setSuccessSubmit(true);
            setTimeout(() => {
                onSuccess?.(data);
                close?.();
            }, 600);
            return;
        }
        const message =
            error?.response?.data?.message ||
            error?.response?.data ||
            (typeof error === 'string' ? error : 'Failed to split shipment');
        setSubmitError(String(message));
    };

    return (
        <>
            <DialogTitle>
                Split Order
                <div>
                    <IconButton
                        onClick={close}
                        sx={{
                            position: 'absolute', width: '40px', height: '40px', top: '10px', right: '30px',
                            boxShadow: "rgba(0, 0, 0, 0.16) 0px 1px 4px",
                            backgroundColor: 'white', zIndex: 100, "&:hover": { backgroundColor: '#f0f0f0' }
                        }}
                    >
                        <CloseIcon sx={{ cursor: 'pointer' }} />
                    </IconButton>
                </div>
            </DialogTitle>
            <hr style={{ margin: 0 }} />
            <DialogContent>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 16, paddingTop: 8 }}>
                    <Typography variant='subtitle2'>Order Num: {orderNum}</Typography>
                    <Typography variant='body2' color="text.secondary">
                        Allocate all {originalQuantity} units across at least two shipments.
                    </Typography>
                    <TextField
                        label="Item Name"
                        value={originalItemTitle}
                        fullWidth
                        InputProps={{
                            readOnly: true,
                        }}
                        variant="outlined"
                        size="small"
                    />
                    {splitShipments.map((shipment, index) => (
                        <div key={shipment.id} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                            <TextField
                                label={`Shipment ${index + 1} Quantity`}
                                type="number"
                                value={shipment.quantity}
                                onChange={(e) => handleShipmentQuantityChange(shipment.id, e.target.value)}
                                fullWidth
                                variant="outlined"
                                size="small"
                                inputProps={{ min: 1 }}
                                helperText={
                                    index === 0
                                        ? `Allocated ${totalAllocated} of ${originalQuantity}` +
                                          (remaining === 0 ? '' : ` (${remaining} remaining)`)
                                        : undefined
                                }
                            />
                            {splitShipments.length > 2 && (
                                <IconButton aria-label="remove" size="small" color="error" onClick={() => handleRemoveShipment(shipment.id)}>
                                    <DeleteIcon fontSize="small" />
                                </IconButton>
                            )}
                            {index === splitShipments.length - 1 && (
                                <IconButton
                                    aria-label="add"
                                    size="small"
                                    sx={{ color: '#3E5C76' }}
                                    onClick={handleAddShipment}
                                    disabled={remaining <= 0}
                                >
                                    <AddIcon fontSize="small" />
                                </IconButton>
                            )}
                        </div>
                    ))}
                    {submitError ? (
                        <Typography variant="body2" color="error">
                            {submitError}
                        </Typography>
                    ) : null}
                </div>
            </DialogContent>
            <DialogActions>
                <Button
                    variant="contained"
                    loading={submitLoad}
                    disableElevation
                    disabled={submitLoad || !canSubmit || successSubmit}
                    sx={{ textTransform: 'none', minWidth: '200px', background: successSubmit ? 'green' : '#FF8C61' }}
                    onClick={handleSubmitSplitOrder}
                >
                    {successSubmit ?
                        <>
                            <span>Success</span>
                            <CheckOutlinedIcon fontSize="small"
                                sx={{ color: 'white', marginLeft: '10px' }} />
                        </>
                        : "Create Split Shipments"
                    }
                </Button>
            </DialogActions>
        </>
    );
}
